/* ============================================================
   CAUCUS — Shopify Theme JavaScript
   Vanilla JS replacing React / Framer Motion
   ============================================================ */

(function () {
  'use strict';

  /* ── Intersection Observer (fade-up animations) ─────────── */
  function initAnimations() {
    const els = document.querySelectorAll('.anim-fade-up');
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '-60px' }
    );
    els.forEach((el) => observer.observe(el));
  }

  /* ── Navbar ─────────────────────────────────────────────── */
  function initNavbar() {
    const header = document.querySelector('.site-header');
    const hamburger = document.querySelector('.site-header__hamburger');
    const mobileMenu = document.querySelector('.mobile-menu');
    if (!header) return;

    // Scroll behaviour
    function onScroll() {
      if (window.scrollY > 40) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Mobile menu toggle
    if (hamburger && mobileMenu) {
      hamburger.addEventListener('click', () => {
        const isOpen = mobileMenu.classList.toggle('open');
        hamburger.classList.toggle('open', isOpen);
        document.body.style.overflow = isOpen ? 'hidden' : '';
      });
      // Close on link click
      mobileMenu.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', () => {
          mobileMenu.classList.remove('open');
          hamburger.classList.remove('open');
          document.body.style.overflow = '';
        });
      });
    }
  }

  /* ── Hero Slideshow ─────────────────────────────────────── */
  function initHero() {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    const slides = hero.querySelectorAll('[data-slide]');
    const dots = hero.querySelectorAll('.hero__dot');
    const counter = hero.querySelector('.hero__counter');
    if (!slides.length) return;

    let current = 0;
    let timer = null;

    function goTo(index) {
      slides[current].style.opacity = '0';
      slides[current].style.pointerEvents = 'none';
      dots[current]?.classList.remove('active');

      current = (index + slides.length) % slides.length;

      slides[current].style.opacity = '1';
      slides[current].style.pointerEvents = 'auto';
      dots[current]?.classList.add('active');

      if (counter) {
        counter.textContent = `0${current + 1} / 0${slides.length}`;
      }
    }

    // Set initial styles
    slides.forEach((slide, i) => {
      slide.style.transition = 'opacity 0.7s ease';
      slide.style.opacity = i === 0 ? '1' : '0';
      slide.style.position = i === 0 ? 'relative' : 'absolute';
      slide.style.inset = '0';
      slide.style.pointerEvents = i === 0 ? 'auto' : 'none';
    });
    // Wrap slides in relative container handled in liquid

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        clearInterval(timer);
        goTo(i);
        timer = setInterval(() => goTo(current + 1), 6500);
      });
    });

    // Auto-advance
    timer = setInterval(() => goTo(current + 1), 6500);
  }

  /* ── Testimonials ───────────────────────────────────────── */
  function initTestimonials() {
    const section = document.querySelector('.testimonials-section');
    if (!section) return;

    const cards = section.querySelectorAll('[data-testimonial]');
    const dots = section.querySelectorAll('.testimonial-nav__dot');
    const sideItems = section.querySelectorAll('.testimonial-sidebar__item');
    const countEl = section.querySelector('.testimonial-nav__count');
    if (!cards.length) return;

    let active = 0;

    function setActive(index) {
      cards[active]?.classList.remove('active');
      cards[active] && (cards[active].style.display = 'none');
      dots[active]?.classList.remove('active');
      sideItems[active]?.classList.remove('active');

      active = index;

      cards[active] && (cards[active].style.display = 'block');
      dots[active]?.classList.add('active');
      sideItems[active]?.classList.add('active');

      if (countEl) countEl.textContent = `0${active + 1} / 0${cards.length}`;
    }

    // Init
    cards.forEach((c, i) => { c.style.display = i === 0 ? 'block' : 'none'; });
    dots[0]?.classList.add('active');
    sideItems[0]?.classList.add('active');

    dots.forEach((dot, i) => dot.addEventListener('click', () => setActive(i)));
    sideItems.forEach((item, i) => item.addEventListener('click', () => setActive(i)));
  }

  /* ── Product image gallery ──────────────────────────────── */
  function initProductGallery() {
    const mainImg = document.querySelector('.product-page__main-img img');
    const thumbs = document.querySelectorAll('.product-page__thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const img = thumb.querySelector('img');
        const full = img?.getAttribute('data-full') || img?.src;
        if (full) {
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = full;
        }
        thumbs.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });

    thumbs[0]?.classList.add('active');
  }

  /* ── Parallax (lightweight, on scroll) ─────────────────── */
  function initParallax() {
    const targets = document.querySelectorAll('[data-parallax]');
    if (!targets.length) return;

    function onScroll() {
      targets.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const speed = parseFloat(el.dataset.parallax) || 0.1;
        const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * speed;
        el.style.transform = `translateY(${offset}px)`;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Add to Cart (Shopify AJAX) ─────────────────────────── */
  function initAddToCart() {
    const forms = document.querySelectorAll('[data-product-form]');
    forms.forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[data-add-btn]');
        const variantId = form.querySelector('[name="id"]')?.value;
        if (!variantId || !btn) return;

        btn.textContent = 'Adding...';
        btn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
          if (res.ok) {
            btn.textContent = 'Added ✓';
            setTimeout(() => {
              btn.textContent = 'Add to Bag';
              btn.disabled = false;
            }, 2000);
            // Update cart count
            updateCartCount();
          } else {
            btn.textContent = 'Try Again';
            btn.disabled = false;
          }
        } catch {
          btn.textContent = 'Error';
          btn.disabled = false;
        }
      });
    });
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const countEl = document.querySelector('[data-cart-count]');
      if (countEl) {
        countEl.textContent = cart.item_count;
        countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }
    } catch {}
  }

  /* ── Newsletter form ────────────────────────────────────── */
  function initNewsletter() {
    const form = document.querySelector('[data-newsletter-form]');
    if (!form) return;
    // Let the form submit normally to Shopify — just provide visual feedback
    form.addEventListener('submit', () => {
      const btn = form.querySelector('button[type="submit"]');
      if (btn) {
        btn.textContent = 'Subscribing...';
        btn.disabled = true;
      }
    });
  }

  /* ── Init all ───────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initAnimations();
    initNavbar();
    initHero();
    initTestimonials();
    initProductGallery();
    initParallax();
    initAddToCart();
    initNewsletter();
    updateCartCount();
  });
})();
